import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { IoPinOutline as PinIcon } from 'react-icons/io5';
import 'leaflet/dist/leaflet.css';

import useGeoLocation from './utils/useGeoLocation';
import classnames from 'classnames';
import useReverseGeoCoding from './utils/useReverseGeoCoding';
import { extractTownName, extragWgsPosition } from '@carlito/geo-utils';
import useGeoCoding from './utils/useGeoCoding';
import { Wgs84Location } from '@carlito/geo-utils';
import { MapContainer, TileLayer, Marker, useMap, ZoomControl } from 'react-leaflet';
import { DragEndEvent, Icon } from 'leaflet';

import mapMarkerImg from './map_pin.svg';
import { useTranslation } from 'react-i18next';

const mapIcon = new Icon({
  iconUrl: mapMarkerImg,
  iconAnchor: [12, 41],
});

/**
 * Turns a long location name into a small,
 * easy digestable one
 */
function sanitizeLocationName(location: string) {
  const splitted = location.split(',');
  return splitted[0];
}

export interface LocationSelectValue {
  label: string;
  value: Wgs84Location;
}

interface Props {
  /**
   * Defaults to true.
   * Turns a long location name into a small,
   * easy digestable one
   */
  sanitizeLocationName?: boolean;
  onLocationSelected: (selectValue: LocationSelectValue) => void;
}

interface MapApiProps {
  currentLocation?: Wgs84Location;
}

/**
 * This component is needed
 * because useMap is only available
 * inside the MapContainer, which
 * is a Context Provider
 */
const MapApi: React.FC<MapApiProps> = ({ currentLocation }) => {
  const map = useMap();

  useEffect(() => {
    if (currentLocation) {
      map.setView([currentLocation.lat, currentLocation.lon]);
    }
  }, [currentLocation, map]);

  return <></>;
};

const INITIAL_MAP_CENTER: Wgs84Location = {
  lat: 46.7177,
  lon: 11.6572,
};

export const GeoLocationRetrieval = ({
  onLocationSelected,
  sanitizeLocationName: shouldSanitizeLocationName = true,
}: Props) => {
  const { t } = useTranslation();

  const [locationSearchTerm, setLocationSearchTerm] = useState<string>();
  const { state, position: userGeoPosition, retrieveLocation } = useGeoLocation();
  const [currentLocation, setCurrentLocation] = useState<Wgs84Location>(INITIAL_MAP_CENTER);
  const [selectValue, setSelectValue] = useState<LocationSelectValue>();

  const {
    state: reverseGeoCodingState,
    result: reverseGeoCodedUserLocation,
    reverseGeoCode,
  } = useReverseGeoCoding();
  const { state: geoCodingState, result: geoCodingResult } = useGeoCoding(locationSearchTerm);

  const optionsLoading = geoCodingState === 'loading' || reverseGeoCodingState === 'loading';

  // if user geo location is known, reverse geo code it's location to get it's name
  useEffect(() => {
    if (userGeoPosition) {
      reverseGeoCode(userGeoPosition);
    }
  }, [userGeoPosition]);

  const townName = reverseGeoCodedUserLocation
    ? extractTownName(reverseGeoCodedUserLocation.address) ??
      reverseGeoCodedUserLocation.display_name
    : undefined;

  const reverseWgsPosition = reverseGeoCodedUserLocation
    ? extragWgsPosition(reverseGeoCodedUserLocation)
    : undefined;

  // Set town name retrieved by GeoLocation API
  // as selected value if defined
  useEffect(() => {
    if (!townName || !reverseWgsPosition) return;
    setSelectValue({
      value: reverseWgsPosition,
      label: townName,
    });
  }, [townName]);

  useEffect(() => {
    if (selectValue?.value) {
      setCurrentLocation(selectValue.value);
    }
  }, [selectValue?.value]);

  let options: LocationSelectValue[] =
    geoCodingResult?.map((result) => ({
      label: result.display_name,
      value: extragWgsPosition(result),
    })) ?? [];

  if (townName && reverseWgsPosition) {
    options = [
      {
        value: reverseWgsPosition,
        label: townName,
      },
      ...options,
    ];
  }

  function onMarkerDragend(e: DragEndEvent) {
    const targetLocation = e.target.getLatLng() as { lat: number; lng: number };
    const markerCoords: Wgs84Location = { lat: targetLocation.lat, lon: targetLocation.lng };
    reverseGeoCode(markerCoords);
    setCurrentLocation(markerCoords);
  }

  useEffect(() => {
    if (selectValue) {
      // sanitize location name
      let label = selectValue.label;
      if (shouldSanitizeLocationName) {
        label = sanitizeLocationName(selectValue.label);
      }
      onLocationSelected({
        ...selectValue,
        label,
      });
    }
  }, [selectValue?.label, selectValue?.value.lat, selectValue?.value.lon]);

  return (
    <div className="relative h-80 rounded-lg overflow-hidden">
      <div className="flex w-full justify-center items-center absolute top-3 left-0 z-10">
        <Select
          placeholder={t('Search location...')}
          noOptionsMessage={() => t('Type something to search for a location')}
          className="w-4/6 mr-4"
          value={selectValue}
          options={options}
          // Prevents "list sometimes empty" bug
          // https://stackoverflow.com/questions/40868323/react-select-sometimes-doesnt-display-the-options-in-the-dropdown-menu-on-async
          filterOption={false as any}
          isLoading={optionsLoading}
          onInputChange={(val) => {
            if (val) {
              setLocationSearchTerm(val);
            }
          }}
          onChange={(val) => {
            if (val) {
              setSelectValue(val);
            }
          }}
        />
        <button
          type="button"
          className={classnames(
            { 'bg-indigo-600 hover:bg-indigo-700': state === 'empty' },
            { 'bg-green-500 hover:bg-green-600': state === 'ready' },
            { 'bg-red-600 hover:bg-red-700': state === 'error' },
            {
              'bg-gray-500 hover:bg-gray-700 opacity-40':
                state === 'loading' || reverseGeoCodingState === 'loading',
            },
            'inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
          )}
          onClick={retrieveLocation}
        >
          <PinIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="absolute top-0 left-0 w-full h-full z-0">
        <MapContainer
          className="h-full"
          center={[currentLocation.lat, currentLocation.lon]}
          zoom={13}
          scrollWheelZoom={false}
          zoomControl={false}
          attributionControl={false}
        >
          <ZoomControl position="bottomleft" />
          <MapApi currentLocation={currentLocation} />
          <TileLayer
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            eventHandlers={{ dragend: onMarkerDragend }}
            draggable
            position={[currentLocation.lat, currentLocation.lon]}
            icon={mapIcon}
          />
        </MapContainer>
      </div>
    </div>
  );
};
